import React, { useState, useEffect } from "react";
import { Button, Card, CardBody, Col, Row, Form } from "react-bootstrap";
import { JobCategoriesService } from "./service";
import BasicModal from "../../Common/BasicModal";
import DeleteModal from "../../Common/DeleteModal";
import { ErrorToast, SuccessToast } from "../../shared/toasters/toasters";
import { useTheme } from "../../context/ThemeContext";
import { useTranslation } from "react-i18next";
import Accordion from "react-bootstrap/Accordion";

function mapWithIsExpanded(nodes: any[], search: string): any[] {
  const searchTerm = search.trim().toLowerCase();
  if (!searchTerm) {
    // If search is empty, nothing should be expanded
    return nodes.map(node => ({
      ...node,
      isExpanded: false,
      children: node.children ? mapWithIsExpanded(node.children, search) : undefined
    }));
  }

  // Helper to check if any descendant matches
  function hasDescendantMatch(node: any): boolean {
    if (Array.isArray(node.children) && node.children.length > 0) {
      return node.children.some((child: any) =>
        (child.title && child.title.toLowerCase().includes(searchTerm)) ||
        hasDescendantMatch(child)
      );
    }
    return false;
  }

  return nodes.map(node => {
    const hasChildren = Array.isArray(node.children) && node.children.length > 0;
    const shouldExpand = hasChildren && hasDescendantMatch(node);

    return {
      ...node,
      isExpanded: shouldExpand,
      children: hasChildren ? mapWithIsExpanded(node.children, search) : undefined
    };
  });
}
// Helper to collect all eventKeys for nodes with isExpanded === true
const collectExpandedKeys = (categories: any[], prefix = ""): string[] => {
  let keys: string[] = [];
  categories.forEach((cat, idx) => {
    const key = prefix ? `${prefix}-${idx}` : `${idx}`;
    if (cat.isExpanded) {
      // Add all parent keys in the path to this node
      if (prefix) {
        const parts = prefix.split("-");
        let path = "";
        for (let i = 0; i < parts.length; i++) {
          path = path ? `${path}-${parts[i]}` : parts[i];
          if (!keys.includes(path)) keys.push(path);
        }
      }
      // Add this node's key if it has children
      if (cat.children && cat.children.length > 0) {
        if (!keys.includes(key)) keys.push(key);
      }
    }
    if (cat.children) {
      keys = keys.concat(collectExpandedKeys(cat.children, key));
    }
  });
  return keys;
};

const JobCategoryList: React.FC = () => {
  const { theme } = useTheme(); // 'theme' could be 'light' or 'dark'
  const { t } = useTranslation(); // Initialize translation hook

  const [titleSearch, setTitleSearch] = useState('');
  const [debouncedTitle, setDebouncedTitle] = useState(titleSearch);
  const [categories, setCategories] = useState<any[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currentCategory, setCurrentCategory] = useState<any>(null);
  const [title, setTitle] = useState("");
  const [parent, setParent] = useState<number | null>(null);
  const [categoryToDelete, setCategoryToDelete] = useState<number | null>(null);
  const [activeKeys, setActiveKeys] = useState<string[]>([]);

  // Debounce search
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedTitle(titleSearch), 400);
    return () => clearTimeout(handler);
  }, [titleSearch]);

  // Fetch categories on debounced search
  useEffect(() => {
    fetchCategories();
    // eslint-disable-next-line
  }, [debouncedTitle]);
const fetchCategories = async () => {
  try {
    const response = await JobCategoriesService.getAll({ title: debouncedTitle });
    const mapped = mapWithIsExpanded(response, debouncedTitle);
    setCategories(mapped);

    // Auto-expand parents if any node isExpanded
    const autoExpandKeys = collectExpandedKeys(mapped);
    setActiveKeys(autoExpandKeys);
  } catch (error) {
    ErrorToast(error);
  }
};
  const handleOpenModal = (category: any = null, parentCategory: any = null) => {
    setCurrentCategory(category);
    setTitle(category ? category.title : "");
    setParent(parentCategory ? parentCategory.id : null);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setCurrentCategory(null);
    setTitle("");
    setParent(null);
  };

  const handleSaveChanges = async () => {
    try {
      const data = { title, parent: parent !== null ? parent : null };
      if (currentCategory) {
        await JobCategoriesService.update(data, currentCategory.id);
        SuccessToast(t("jobCategory.successUpdate"));
      } else {
        await JobCategoriesService.create(data);
        SuccessToast(t("jobCategory.successCreate"));
      }
      fetchCategories();
      handleCloseModal();
    } catch (error) {
      ErrorToast(error);
    }
  };

  const handleOpenDeleteModal = (categoryId: number) => {
    setCategoryToDelete(categoryId);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setCategoryToDelete(null);
  };

  const handleDeleteCategory = async () => {
    if (categoryToDelete !== null) {
      try {
        await JobCategoriesService.delete(categoryToDelete);
        SuccessToast(t("jobCategory.successDelete"));
        fetchCategories();
        handleCloseDeleteModal();
      } catch (error) {
        ErrorToast(error);
      }
    }
  };

  // Example color logic (customize as needed)
  const getBgColor = (level: number) => {
    if (level === 1) return theme === "dark" ? "#222" : "#e9f5ff";
    if (level === 2) return theme === "dark" ? "#333" : "#f0f7ff";
    return theme === "dark" ? "#222" : "#f8f9fa";
  };

  // Helper to generate unique keys for nested accordions
  const getEventKey = (prefix: string, idx: number) => (prefix ? `${prefix}-${idx}` : `${idx}`);

  // Handler for manual expand/collapse
  const handleAccordionToggle = (eventKey: string) => {
    setActiveKeys(prev =>
      prev.includes(eventKey) ? prev.filter(key => key !== eventKey) : [...prev, eventKey]
    );
  };

  const renderCategories = (categories: any[], level: number = 0, prefix: string = "") =>{
    
  
    return (
    <Accordion alwaysOpen activeKey={activeKeys}>
      {categories.map((category, idx) => {
        const eventKey = getEventKey(prefix, idx);

        // Level 3: Not collapsible, colored background
        if (category.level === 3) {
          return (
            <div
              key={category.id}
              className="d-flex justify-content-between align-items-center py-2 px-3 mb-1"
              style={{
                background: getBgColor(3),
                borderRadius: "6px",
              }}
            >
              <span style={{ color: theme === "dark" ? "#fff" : "#000" }}>
                {category.title}
              </span>
              <div className="d-flex">
                <li
                  className="list-inline-item"
                  role="button"
                  onClick={() => handleOpenModal(category)}
                >
                  <span className="avtar avtar-s btn-link-success btn-pc-default">
                    <i
                      className="ti ti-edit f-20"
                      style={{ color: theme === "dark" ? "#fff" : "#000" }}
                    ></i>
                  </span>
                </li>
                <li className="list-inline-item">
                  <span
                    role="button"
                    className="avtar avtar-s btn-link-danger btn-pc-default"
                    onClick={() => handleOpenDeleteModal(category.id)}
                  >
                    <i
                      className="ti ti-trash f-20"
                      style={{ color: theme === "dark" ? "#fff" : "#000" }}
                    ></i>
                  </span>
                </li>
              </div>
            </div>
          );
        }

        // If no children, render as a styled div (no collapse icon)
        if (!category.children || category.children.length === 0) {
          return (
            <div
              key={category.id}
              className="d-flex justify-content-between align-items-center py-2 px-3 mb-1"
              style={{
                background: getBgColor(category.level),
                borderRadius: "6px",
              }}
            >
              <span style={{ color: theme === "dark" ? "#fff" : "#000" }}>
                {category.title}
              </span>
              <div className="d-flex">
                {category.level !== 3 && (
                  <li
                    className="list-inline-item"
                    onClick={() => handleOpenModal(null, category)}
                  >
                    <span
                      role="button"
                      className="avtar avtar-s btn-link-success btn-pc-default"
                    >
                      <i
                        className="ti ti-plus f-20"
                        style={{ color: theme === "dark" ? "#fff" : "#000" }}
                      ></i>
                    </span>
                  </li>
                )}
                <li
                  className="list-inline-item"
                  role="button"
                  onClick={() => handleOpenModal(category)}
                >
                  <span className="avtar avtar-s btn-link-success btn-pc-default">
                    <i
                      className="ti ti-edit f-20"
                      style={{ color: theme === "dark" ? "#fff" : "#000" }}
                    ></i>
                  </span>
                </li>
                <li className="list-inline-item">
                  <span
                    role="button"
                    className="avtar avtar-s btn-link-danger btn-pc-default"
                    onClick={() => handleOpenDeleteModal(category.id)}
                  >
                    <i
                      className="ti ti-trash f-20"
                      style={{ color: theme === "dark" ? "#fff" : "#000" }}
                    ></i>
                  </span>
                </li>
              </div>
            </div>
          );
        }

        // Otherwise, render as Accordion.Item (collapsible)
        return (
          <Accordion.Item eventKey={eventKey} key={category.id}>
            <Accordion.Header
              style={{
                background: getBgColor(category.level),
                borderRadius: "6px",
                color: theme === "dark" ? "#fff" : "#000",
              }}
              onClick={e => {
                e.stopPropagation();
                handleAccordionToggle(eventKey);
              }}
            >
              <div className="d-flex justify-content-between align-items-center w-100">
                <span style={{ color: theme === "dark" ? "#fff" : "#000" }}>
                  {category.title}
                </span>
                <div className="d-flex">
                  {category.level !== 3 && (
                    <li
                      className="list-inline-item"
                      onClick={e => {
                        e.stopPropagation();
                        handleOpenModal(null, category);
                      }}
                    >
                      <span
                        role="button"
                        className="avtar avtar-s btn-link-success btn-pc-default"
                      >
                        <i
                          className="ti ti-plus f-20"
                          style={{ color: theme === "dark" ? "#fff" : "#000" }}
                        ></i>
                      </span>
                    </li>
                  )}
                  <li
                    className="list-inline-item"
                    role="button"
                    onClick={e => {
                      e.stopPropagation();
                      handleOpenModal(category);
                    }}
                  >
                    <span className="avtar avtar-s btn-link-success btn-pc-default">
                      <i
                        className="ti ti-edit f-20"
                        style={{ color: theme === "dark" ? "#fff" : "#000" }}
                      ></i>
                    </span>
                  </li>
                  <li className="list-inline-item">
                    <span
                      role="button"
                      className="avtar avtar-s btn-link-danger btn-pc-default"
                      onClick={e => {
                        e.stopPropagation();
                        handleOpenDeleteModal(category.id);
                      }}
                    >
                      <i
                        className="ti ti-trash f-20"
                        style={{ color: theme === "dark" ? "#fff" : "#000" }}
                      ></i>
                    </span>
                  </li>
                </div>
              </div>
            </Accordion.Header>
            <Accordion.Body>
              {category.children && renderCategories(category.children, level + 1, eventKey)}
            </Accordion.Body>
          </Accordion.Item>
        );
      })}
    </Accordion>
 ) };

  return (
    <React.Fragment>
      <Row>
        <Col sm={12}>
          <Card bg={theme === "dark" ? "dark" : "light"}>
            <CardBody>
              <div className="text-end mb-3">
                <Button variant="primary" onClick={() => handleOpenModal()}>
                  {t("jobCategory.addCategory")}
                </Button>
              </div>
              <div className="mb-3 d-flex justify-content-end">
                <input
                  type="search"
                  className="form-control w-auto"
                  placeholder={t("jobCategory.searchPlaceholder") || "Search by title"}
                  value={titleSearch}
                  onChange={e => setTitleSearch(e.target.value)}
                  style={{ minWidth: 250 }}
                />
              </div>
              <div>{renderCategories(categories)}</div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <BasicModal
        show={showModal}
        handleClose={handleCloseModal}
        handleSave={handleSaveChanges}
        title={
          currentCategory
            ? t("jobCategory.editCategory")
            : t("jobCategory.addCategory")
        }
      >
        <Form>
          <Form.Group controlId="formTitle">
            <Form.Label>{t("jobCategory.title")}</Form.Label>
            <Form.Control
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t("jobCategory.enterTitle")}
            />
          </Form.Group>
        </Form>
      </BasicModal>

      <DeleteModal
        show={showDeleteModal}
        handleClose={handleCloseDeleteModal}
        handleDeleteId={handleDeleteCategory}
        name={t("jobCategory.deleteConfirmation")}
      />
    </React.Fragment>
  );
};

export default JobCategoryList;
